import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  Building2,
  Factory,
  Calendar,
  AlertCircle,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import SEO, { projectSchema } from "@/components/SEO";

const STATUS_TAG = {
  advancing: { label: "Advancing", cls: "bb-tag-positive" },
  permitting: { label: "Permitting", cls: "bb-tag-neutral" },
  delayed: { label: "Delayed", cls: "bb-tag-negative" },
  operational: { label: "Operational", cls: "bb-tag-positive" },
};

const PROJECT_HERO_IMG =
  "https://images.unsplash.com/photo-1709489662983-3674d790b224?w=1600&q=80";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const DataRow = ({ label, value, testId }) => (
  <div
    className="py-4 border-b border-border last:border-b-0 grid grid-cols-12 gap-4"
    data-testid={testId}
  >
    <div className="col-span-12 md:col-span-4 bb-overline text-bb-slate">
      {label}
    </div>
    <div className="col-span-12 md:col-span-8 text-sm text-bb-navy leading-relaxed">
      {value || "—"}
    </div>
  </div>
);

export default function ProjectDetailPage() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [region, setRegion] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);
    setProject(null);
    setCompany(null);
    setRegion(null);
    setArticles([]);

    api
      .get(`/projects/${slug}`)
      .then(async (r) => {
        if (!mounted) return;
        const p = r.data;
        setProject(p);

        const [companiesRes, regionsRes, articlesRes] = await Promise.all([
          api.get("/companies"),
          api.get("/regions"),
          api.get("/articles"),
        ]);
        if (!mounted) return;
        const findById = (list, id) =>
          (Array.isArray(list) ? list : []).find((x) => x.id === id) || null;
        if (p.company) setCompany(findById(companiesRes.data, p.company));
        if (p.region) setRegion(findById(regionsRes.data, p.region));
        const related = (
          Array.isArray(articlesRes.data) ? articlesRes.data : []
        )
          .filter((a) => a.project === p.id)
          .slice(0, 4);
        setArticles(related);
      })
      .catch(() => mounted && setError(true))
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [slug]);

  const tag = useMemo(
    () => (project ? STATUS_TAG[project.status] || STATUS_TAG.advancing : null),
    [project],
  );

  if (loading) {
    return (
      <div
        className="bb-container py-20 text-center bb-overline text-bb-slate"
        data-testid="project-loading"
      >
        Loading project…
      </div>
    );
  }

  if (error || !project) {
    return (
      <section className="bb-container py-20" data-testid="project-not-found">
        <div className="border border-border bg-white p-12 text-center max-w-2xl mx-auto">
          <AlertCircle className="text-bb-copper mx-auto" size={28} />
          <div className="bb-overline text-bb-copper mt-4">404 · Project</div>
          <h1 className="font-archivo text-3xl text-bb-navy mt-3 font-bold">
            Project not found.
          </h1>
          <Link
            to="/projects"
            className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm mt-6"
            data-testid="project-back-to-list"
          >
            <ArrowLeft size={14} /> Back to projects
          </Link>
        </div>
      </section>
    );
  }

  return (
    <article data-testid={`project-detail-${project.slug}`}>
      <SEO
        title={project.name}
        description={project.summary}
        image={PROJECT_HERO_IMG}
        type="article"
        jsonLd={projectSchema(project, company, region)}
      />
      {/* Hero */}
      <header className="bg-white border-b border-border">
        <div className="bb-container py-12 lg:py-16">
          <Link
            to="/projects"
            className="bb-link inline-flex items-center gap-2 text-xs font-archivo uppercase tracking-widest text-bb-navy mb-8"
            data-testid="project-back-link"
          >
            <ArrowLeft size={14} /> Back to Projects
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 flex flex-col">
              <div className="bb-overline text-bb-copper">
                {project.province} · {(project.commodity || []).join(" · ")}
              </div>
              <h1
                className="bb-headline mt-4 text-3xl sm:text-4xl lg:text-5xl text-bb-navy"
                data-testid="project-title"
              >
                {project.name}
              </h1>
              <p className="mt-5 text-base lg:text-lg text-bb-slate leading-relaxed max-w-2xl">
                {project.summary}
              </p>
              <div className="mt-6 flex items-center gap-4 flex-wrap">
                <span
                  className={`text-[11px] font-mono px-3 py-1.5 ${tag.cls}`}
                  data-testid="project-status-tag"
                >
                  {tag.label}
                </span>
                <span className="bb-overline text-bb-slate">
                  Stage · {project.stage}
                </span>
                {project.latestUpdate && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-mono text-bb-slate">
                    <Calendar size={12} /> Updated{" "}
                    {formatDate(project.latestUpdate)}
                  </span>
                )}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="border border-border overflow-hidden">
                <img
                  src={PROJECT_HERO_IMG}
                  alt={project.name}
                  className="bb-img w-full aspect-[4/3] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <section className="py-12 lg:py-20">
        <div className="bb-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Data panel */}
            <div className="lg:col-span-8" data-testid="project-data-panel">
              <div className="bb-overline text-bb-copper">Project Profile</div>
              <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 mb-6 font-bold tracking-tight">
                Key data &amp; status
              </h2>

              <div className="bg-white border border-border p-6 lg:p-8">
                <DataRow
                  label="Operator"
                  value={company ? company.name : project.company}
                  testId="data-operator"
                />
                <DataRow
                  label="Province / Region"
                  value={`${project.province}${region ? " · " + region.name : ""}`}
                  testId="data-province"
                />
                <DataRow
                  label="Commodity"
                  value={(project.commodity || []).join(" · ")}
                  testId="data-commodity"
                />
                <DataRow
                  label="Stage"
                  value={project.stage}
                  testId="data-stage"
                />
                <DataRow
                  label="Capex Estimate"
                  value={
                    project.capexEstimate
                      ? `USD ${project.capexEstimate.toLocaleString()}M (placeholder)`
                      : "—"
                  }
                  testId="data-capex"
                />
                <DataRow
                  label="Production Estimate"
                  value={project.productionEstimate}
                  testId="data-production"
                />
                <DataRow
                  label="Mine Life"
                  value={project.mineLife}
                  testId="data-mine-life"
                />
                <DataRow
                  label="Permitting Status"
                  value={project.permittingStatus}
                  testId="data-permitting"
                />
                <DataRow
                  label="Indigenous Partnership"
                  value={project.indigenousPartnershipStatus}
                  testId="data-indigenous"
                />
                <DataRow
                  label="Infrastructure Needs"
                  value={project.infrastructureNeeds}
                  testId="data-infrastructure"
                />
                {project.location && (
                  <DataRow
                    label="Nearest Community"
                    value={
                      <span className="inline-flex items-center gap-2">
                        <MapPin size={14} className="text-bb-copper" />
                        {project.location.nearestTown}
                        {project.location.lat && project.location.lng && (
                          <span className="font-mono text-xs text-bb-slate ml-2">
                            ({project.location.lat.toFixed(2)},{" "}
                            {project.location.lng.toFixed(2)})
                          </span>
                        )}
                      </span>
                    }
                    testId="data-location"
                  />
                )}
              </div>

              <p className="mt-6 text-xs font-mono text-bb-slate">
                Figures shown are illustrative / placeholder data for the
                BedrockBulletin demo. Refer to operator filings for actuals.
              </p>
            </div>

            {/* Side rail */}
            <aside className="lg:col-span-4 space-y-3" data-testid="project-cross-links">
              <div className="bb-overline text-bb-slate mb-3">In this project</div>
              {company && (
                <Link
                  to={`/companies/${company.slug}`}
                  className="bb-card flex items-center gap-4 p-5 group"
                  data-testid="project-cross-company"
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                    <Building2 size={18} strokeWidth={1.8} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="bb-overline text-bb-copper">Operator</div>
                    <div className="font-archivo font-semibold text-bb-navy truncate">
                      {company.name}
                    </div>
                    <div className="text-[11px] font-mono text-bb-slate mt-0.5">
                      {company.ticker} · {company.exchange}
                    </div>
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-bb-navy group-hover:text-bb-copper transition-colors"
                  />
                </Link>
              )}
              {region && (
                <Link
                  to={`/regions/${region.slug}`}
                  className="bb-card flex items-center gap-4 p-5 group"
                  data-testid="project-cross-region"
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                    <MapPin size={18} strokeWidth={1.8} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="bb-overline text-bb-copper">Region</div>
                    <div className="font-archivo font-semibold text-bb-navy truncate">
                      {region.name}
                    </div>
                  </div>
                  <ArrowUpRight
                    size={16}
                    className="text-bb-navy group-hover:text-bb-copper transition-colors"
                  />
                </Link>
              )}
              {(project.commodity || []).map((c) => (
                <div
                  key={c}
                  className="bb-card flex items-center gap-4 p-5"
                  data-testid={`project-cross-commodity-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 bg-bb-offwhite text-bb-navy border border-border">
                    <Factory size={18} strokeWidth={1.8} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="bb-overline text-bb-copper">Commodity</div>
                    <div className="font-archivo font-semibold text-bb-navy truncate">
                      {c}
                    </div>
                  </div>
                </div>
              ))}

              <div className="pt-6 mt-6 border-t border-border">
                <Link
                  to="/#newsletter"
                  className="bb-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-sm w-full"
                  data-testid="project-newsletter-cta"
                >
                  Get project updates
                  <ArrowUpRight size={14} strokeWidth={2.4} />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related coverage */}
      {articles.length > 0 && (
        <section
          className="py-14 lg:py-20 border-t border-border bg-white"
          data-testid="project-related-coverage"
        >
          <div className="bb-container">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
              <div>
                <div className="bb-overline text-bb-copper">Related coverage</div>
                <h2 className="font-archivo text-2xl lg:text-3xl text-bb-navy mt-2 font-bold tracking-tight">
                  BedrockBulletin on this project
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/news/${a.slug}`}
                  className="bb-card p-6 group flex flex-col gap-2"
                  data-testid={`project-article-${a.slug}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="bb-overline text-bb-copper">
                      {a.category}
                    </span>
                    <span className="text-[11px] font-mono text-bb-slate">
                      {formatDate(a.publishedDate)}
                    </span>
                  </div>
                  <h3 className="font-archivo text-lg text-bb-navy font-semibold tracking-tight leading-snug">
                    <span className="bb-link">{a.title}</span>
                  </h3>
                  <p className="text-sm text-bb-slate leading-relaxed">
                    {a.excerpt}
                  </p>
                  <div className="mt-2 pt-3 border-t border-border flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-bb-slate">
                      <Clock size={11} /> By {a.author}
                    </span>
                    <ArrowUpRight
                      size={14}
                      className="text-bb-navy group-hover:text-bb-copper transition-colors"
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
